import { useRoleLabel } from 'helpers'
import styled from 'styled-components'

import Avatar from 'js/components/Avatar/Avatar'
import { H4 } from 'js/components/Heading'
import Popover from 'js/components/Popover/Popover'
import { USER_ACTION } from 'js/lib/constants'
import { useSiteStore } from 'js/lib/stores/SiteStore'
import { selectViewerGuid, useViewerStore } from 'js/lib/stores/ViewerStore'

import Flexer from '../Flexer/Flexer'

import { Actions } from './components/Actions'
import Fields from './components/Fields'
import Roles from './components/Roles'

interface Props extends React.ComponentProps<typeof Avatar> {
    user: {
        guid: string
        name: string
        icon: string
        vcard: any[]
        roles: string[]
    }
    isOwner: boolean
}

const AvatarContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 300px;
    padding: 16px 16px 10px;
    font-size: ${(p) => p.theme.font.size.tiny};
    line-height: ${(p) => p.theme.font.lineHeight.tiny};
`

const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${H4} {
        margin-bottom: 8px;
    }
`

const AvatarTrigger = styled.button`
    border-radius: 50%;
    outline-offset: 2px;
`

const Divider = styled.div`
    height: 1px;
    background-color: ${({ theme }) => theme.color.grey[20]};
    margin: 16px -16px 10px;
`

const UserAvatar = ({ user, isOwner, ...rest }: Props) => {
    const viewerGuid = useViewerStore(selectViewerGuid)

    const chatEnabled = useSiteStore((state) =>
        Boolean(state.site?.chatEnabled),
    )

    const integratedVideocallEnabled = false /* useSiteStore((state) =>
        Boolean(state.site?.integratedVideocallEnabled),
    ) */ // TODO: Uncomment this when the videocall is enabled

    const { name, icon, vcard, roles } = user

    const isViewer = viewerGuid === user.guid

    const ownerHasVcard = vcard?.length > 0

    const ownerHasRoles = roles?.length > 0

    const actions: string[] = [
        ...(chatEnabled ? [USER_ACTION.Chat] : []),
        ...(integratedVideocallEnabled ? [USER_ACTION.VideoCall] : []),
    ]

    const ownerHasActions = actions?.length > 0

    const footerIsVisible = ownerHasRoles || (ownerHasActions && !isViewer)

    const actionsIsVisible = !isViewer

    const role = useRoleLabel(roles, isOwner)

    const AvatarComponent = (
        <Avatar
            disabled
            role={role}
            showHoverOverlay={ownerHasVcard}
            name={name}
            image={icon}
            {...rest}
        />
    )

    if (!ownerHasVcard) {
        return AvatarComponent
    }

    return (
        <Popover
            trigger="mouseenter focusin"
            touch={['hold', 350]}
            delay
            content={
                <AvatarContentWrapper data-testid="user-avatar-popover-content">
                    <Header>
                        <Avatar
                            disabled
                            name={name}
                            image={icon}
                            size="normal"
                        />
                        <H4>{name}</H4>
                    </Header>
                    <Fields vcard={vcard} />
                    {footerIsVisible && (
                        <>
                            <Divider />
                            <Flexer
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                <Roles roles={roles} isOwner={isOwner} />
                                {actionsIsVisible && (
                                    <Actions actions={actions} user={user} />
                                )}
                            </Flexer>
                        </>
                    )}
                </AvatarContentWrapper>
            }
        >
            <AvatarTrigger
                data-testid="user-avatar-popover-trigger"
                tabIndex={0}
                type="button"
            >
                {AvatarComponent}
            </AvatarTrigger>
        </Popover>
    )
}

export default UserAvatar
